/**
 *
 * Lazily-initialized UserManager using oidc-client-ts.
 * AWS recommends this library for direct OIDC/PKCE flows with Cognito Hosted UI.
 *
 * We use a getter instead of a top-level instance so that oidc-client-ts is NOT
 * initialized when VUE_APP_USE_COGNITO_AUTH=false (Frontegg path).  Top-level
 * construction caused "[object Event]" webpack load errors on the Frontegg path
 *
 * We also supply `metadata` explicitly to skip the automatic OIDC discovery
 * fetch — Cognito's .well-known/openid-configuration blocks cross-origin requests
 * from the browser, causing "Failed to fetch" on signinRedirect.
 */

import { Log, UserManager, WebStorageStateStore } from "oidc-client-ts";

let _userManager: UserManager | null = null;
/** PKCE redirect to Cognito `/signup` (managed login) instead of `/oauth2/authorize` → login. */
let _signupHostedUiUserManager: UserManager | null = null;

/** Verbose oidc-client-ts traces (SigninState, redirect). Set VUE_APP_COGNITO_DEBUG_OIDC=true. */
function applyOidcClientDebugLogging(): void {
  if (process.env.VUE_APP_COGNITO_DEBUG_OIDC !== "true") {
    return;
  }
  Log.setLogger(console);
  Log.setLevel(Log.DEBUG);
}

/**
 * @param hostedUiAuthPath Cognito path after domain: `oauth2/authorize` (sign-in) or `signup` (register).
 *        See https://docs.aws.amazon.com/cognito/latest/developerguide/managed-login-endpoints.html
 */
function buildUserManager(hostedUiAuthPath: string): UserManager {
  applyOidcClientDebugLogging();
  const region = process.env.VUE_APP_COGNITO_REGION as string;
  const userPoolId = process.env.VUE_APP_COGNITO_USER_POOL_ID as string;
  const domain = process.env.VUE_APP_COGNITO_DOMAIN as string; // no https://
  const clientId = process.env.VUE_APP_COGNITO_CLIENT_ID as string;
  const redirectUri = process.env.VUE_APP_COGNITO_REDIRECT_SIGN_IN as string;

  if (!region || !userPoolId || !domain || !clientId || !redirectUri) {
    throw new Error(
      "Missing Cognito env: VUE_APP_COGNITO_REGION, VUE_APP_COGNITO_USER_POOL_ID, VUE_APP_COGNITO_DOMAIN, VUE_APP_COGNITO_CLIENT_ID, VUE_APP_COGNITO_REDIRECT_SIGN_IN"
    );
  }

  const issuer = `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`;
  const hostedUiBase = `https://${domain}`;

  return new UserManager({
    // authority is still required by oidc-client-ts for state validation
    authority: issuer,
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    // Include `profile` so the ID token / userinfo can carry `name`, `given_name`, `family_name`.
    // `aws.cognito.signin.user.admin` is required for access-token DeleteUser (failed invite cleanup).
    // Enable both on the Cognito app client: Hosted UI → OAuth 2.0 scopes.
    scope: "openid email phone profile aws.cognito.signin.user.admin",

    // Store tokens in localStorage (survives full-page redirects)
    userStore: new WebStorageStateStore({ store: window.localStorage }),

    // Store OIDC state/PKCE verifier in localStorage too.
    // Default is sessionStorage which is CLEARED after Cognito's full-page
    // redirect back to the app, causing "No matching state found in storage".
    stateStore: new WebStorageStateStore({ store: window.localStorage }),

    automaticSilentRenew: false,

    // Provide endpoints directly — prevents the discovery fetch that fails due to CORS
    metadata: {
      issuer,
      authorization_endpoint: `${hostedUiBase}/${hostedUiAuthPath}`,
      token_endpoint: `${hostedUiBase}/oauth2/token`,
      userinfo_endpoint: `${hostedUiBase}/oauth2/userInfo`,
      end_session_endpoint: `${hostedUiBase}/logout`,
      jwks_uri: `${issuer}/.well-known/jwks.json`,
    },
  });
}

/** Returns the shared UserManager instance, creating it on first call. */
export function getUserManager(): UserManager {
  if (!_userManager) {
    _userManager = buildUserManager("oauth2/authorize");
  }
  return _userManager;
}

/**
 * UserManager for the **sign-up** Hosted UI page (`/signup`). Same PKCE/state storage as {@link getUserManager};
 * use only for `signinRedirect`; callback uses `cognitoSigninCallbackFromRedirect` in `auth.ts`.
 */
export function getUserManagerForSignupHostedUi(): UserManager {
  if (!_signupHostedUiUserManager) {
    _signupHostedUiUserManager = buildUserManager("signup");
  }
  return _signupHostedUiUserManager;
}
